import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { NzModalModule } from 'ng-zorro-antd/modal';

import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NZ_ICONS } from 'ng-zorro-antd/icon';
import {
  // ── Navegación y layout ──────────────────────────────────────────────
  UserOutline,
  LockOutline,
  MenuUnfoldOutline,
  MenuFoldOutline,
  HomeOutline,
  LogoutOutline,
  LeftOutline,
  RightOutline,
  ArrowLeftOutline,
  // ── Empleados y contactos ────────────────────────────────────────────
  ContactsOutline,
  TeamOutline,
  UserAddOutline,
  // ── Acciones CRUD ────────────────────────────────────────────────────
  EyeOutline,
  EditOutline,
  DeleteOutline,
  PlusOutline,
  PlusCircleOutline,
  CloseOutline,
  CloseCircleOutline,
  CloseCircleFill,
  SelectOutline,
  SyncOutline,
  ReloadOutline,
  // ── Búsqueda y filtrado ──────────────────────────────────────────────
  SearchOutline,
  FilterOutline,
  // ── Estado / feedback ────────────────────────────────────────────────
  CheckOutline,
  ClearOutline,
  CheckCircleOutline,
  CheckCircleFill,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  CheckSquareOutline,
  InfoCircleOutline,
  WarningOutline,
  StopOutline,
  // Icono por defecto de <nz-popconfirm> (lo pinta con nzTheme="fill").
  // Sin registrarlo, el popconfirm sale sin icono.
  ExclamationCircleFill,
  // ── Vacaciones y calendario ──────────────────────────────────────────
  CalendarOutline,
  CarryOutOutline,
  ScheduleOutline,
  HistoryOutline,
  ClockCircleOutline,
  HourglassOutline,
  // ── Aprobaciones ─────────────────────────────────────────────────────
  AuditOutline,
  LikeOutline,
  // ── Archivos y exportación ───────────────────────────────────────────
  FileTextOutline,
  FileOutline,
  FilePdfOutline,
  FileExcelOutline,
  DownloadOutline,
  ExportOutline,
  CloudUploadOutline,
  UploadOutline,
  FolderOpenOutline,
  // ── Mensajes y notificaciones ────────────────────────────────────────
  MessageOutline,
  BellOutline,
  // ── Dashboard / gráficas ─────────────────────────────────────────────
  DashboardOutline,
  BarChartOutline,
  LineChartOutline,
  PieChartOutline,
  SmileOutline,
  InboxOutline,
  // ── Organización / jerarquía ─────────────────────────────────────────
  ApartmentOutline,
  PartitionOutline,
  CrownOutline,
  // ── Misc ─────────────────────────────────────────────────────────────
  LoadingOutline,
  SaveOutline,
  SwapOutline,
  CaretUpOutline,
  CaretDownOutline,
  DragOutline,
  HolderOutline,
  FireOutline,
  StarFill,
} from '@ant-design/icons-angular/icons';
import { environment } from 'src/environments/environment';
import {
  credencialesInterceptor,
  proveerSesion,
  sesionExpiradaInterceptor,
} from '@intelishs-group/session-core';
import { NotifyService } from '@intelishs-group/notify-service';
import { proveerI18n, proveerOverlays } from '@intelishs-group/ui';
import { proveerFuenteEmpleados } from './ir-a/fuente-empleados';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    // `NzModalService` NO es `providedIn: 'root'`: lo provee `NzModalModule`. Aqui
    // nadie lo proveia — llegaba DE REBOTE del `<intelishs-file-viewer>` que
    // `app.html` monta en la raiz, porque ese componente importa el modulo en su
    // propio `imports` y eso iza el provider al injector de entorno.
    //
    // Funciona hasta que alguien quita el visor de `app.html`, y entonces se cae
    // algo que no tiene nada que ver con ese cambio. A `ffacturacion` le paso
    // exactamente eso al borrar su `modal-pdf-viewer` local: `tour-button` se
    // quedo sin el servicio que inyecta y que nunca importo el modulo.
    importProvidersFrom(NzModalModule),
    // `credencialesInterceptor` de la librería (session-core >= 5.2.14): pone
    // `withCredentials` en todo salvo en los hosts de `hostsSinCredenciales`
    // (abajo, en `proveerSesion`). Una petición con credenciales a un tercero que
    // responda `Access-Control-Allow-Origin: *` falla por CORS, no por auth.
    provideHttpClient(withInterceptors([credencialesInterceptor, sesionExpiradaInterceptor])),
    provideBrowserGlobalErrorListeners(),
    // Los overlays del CDK vuelven al apilamiento por z-index (ui >= 5.2.7). Sin
    // esto, desde Angular 21 un modal abierto tapa los avisos de SweetAlert y el
    // velo de carga: salen detras, no se pueden pulsar y la pantalla parece colgada.
    proveerOverlays(),
    // Idioma de la plataforma: locale `es-MX` registrado, textos de ng-zorro y
    // `LOCALE_ID` salen de un solo valor (ui >= 5.2.10).
    proveerI18n(),
    // «Ir a…» (Ctrl+K, del shell) busca también empleados, para quien tiene el
    // módulo. En la raíz a propósito: una fuente provista en una ruta no llega al
    // shell. Ver `ir-a/fuente-empleados.ts`.
    proveerFuenteEmpleados(),

    {
      provide: NZ_ICONS,
      useValue: [
        // Navegación y layout
        UserOutline,
        LockOutline,
        MenuUnfoldOutline,
        MenuFoldOutline,
        HomeOutline,
        LogoutOutline,
        LeftOutline,
        RightOutline,
        ArrowLeftOutline,
        // Empleados y contactos
        ContactsOutline,
        TeamOutline,
        UserAddOutline,
        // Acciones CRUD
        EyeOutline,
        EditOutline,
        DeleteOutline,
        PlusOutline,
        PlusCircleOutline,
        CloseOutline,
        CloseCircleOutline,
        CloseCircleFill,
        SelectOutline,
        SyncOutline,
        ReloadOutline,
        // Búsqueda
        SearchOutline,
        FilterOutline,
        // Estado / feedback
        CheckCircleOutline,
        CheckCircleFill,
        CheckCircleTwoTone,
        CloseCircleTwoTone,
        CheckSquareOutline,
        InfoCircleOutline,
        WarningOutline,
        StopOutline,
        ExclamationCircleFill,
        // Vacaciones y calendario
        CalendarOutline,
        CarryOutOutline,
        ScheduleOutline,
        HistoryOutline,
        ClockCircleOutline,
        HourglassOutline,
        // Aprobaciones
        AuditOutline,
        LikeOutline,
        // Archivos y exportación
        FileTextOutline,
        FileOutline,
        FilePdfOutline,
        FileExcelOutline,
        DownloadOutline,
        ExportOutline,
        CloudUploadOutline,
        UploadOutline,
        FolderOpenOutline,
        // Mensajes y notificaciones
        MessageOutline,
        BellOutline,
        // Dashboard / gráficas
        DashboardOutline,
        BarChartOutline,
        LineChartOutline,
        PieChartOutline,
        SmileOutline,
        InboxOutline,
        // Organización / jerarquía
        ApartmentOutline,
        PartitionOutline,
        CrownOutline,
        // Misc
        LoadingOutline,
        SaveOutline,
        SwapOutline,
        CaretUpOutline,
        CaretDownOutline,
        DragOutline,
        HolderOutline,
        FireOutline,
        StarFill,
      ],
    },

    // `LAYOUT_ENV` se fue con `<layout-main>`: lo pedían el shell anterior y su
    // `MenuService`. El shell nuevo no lee nada del inyector y el nombre del
    // sistema entra por `[marca]`.

    // ── La sesión de PLATAFORMA ──────────────────────────────────────────
    //
    // Sustituye a `provideSession({ tokenName })` MÁS las 330 líneas de
    // `session-bootstrap.ts` y `session-fallback.service.ts`, que pedían
    // `/api/Membership/GetTokenInSession`, decodificaban el JWT a mano y
    // reconstruían el usuario claim a claim.
    //
    // Ya no hay JWT que decodificar: las cookies son `HttpOnly` y la aplicación
    // nunca las ve. La sesión se PREGUNTA al servicio de identidad, y se resuelve
    // ANTES de montar el primer componente — así no hay pantalla que se dibuje
    // con datos vacíos y desaparezca.
    proveerSesion({
      identidadUrl: environment.identidadUrl,
      loginUrl: environment.loginUrl || environment.logoutUrl,
      // El aviso antes de mandar al login. Es obligatorio en la libreria y no por
      // gusto: su primera version redirigia en silencio y se comio este aviso,
      // que llevaba años en produccion.
      //
      // El `inject()` aqui FUNCIONA desde 5.0.5. Antes no: esta flecha corre
      // despues del `await` del inicializador, fuera del contexto de inyeccion,
      // asi que lanzaba NG0203 y la libreria se lo tragaba —el aviso no salia y
      // nada lo delataba—. Por eso habia un `SESION_CONFIG` con `useFactory`
      // duplicando esta configuracion justo debajo. Ya no hace falta: el servicio
      // invoca el aviso con `runInInjectionContext`.
      avisarSesionCaducada: () => inject(NotifyService).unauthorizedNotify(),
// A donde va `moduloGuard` cuando falta el modulo. Esta ruta NO lleva
// guard: si lo llevara y tampoco se tuviera, se redirigiria a si misma.
rutaSinPermiso: '/bienvenida',
      // Terceros a los que NO se mandan credenciales: Google APIs contesta
      // `Access-Control-Allow-Origin: *` y CORS rechaza la petición con cookie.
      hostsSinCredenciales: ['googleapis.com'],
    }),
  ],
};
